import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_bulk_confirm } from "../../../constants";
import { toast } from "react-toastify";
import formatDate from "./formatDate";

export default function BulkActionCard({ proposals, sessionUuid, homeStore, onStatusChange }) {
  const [status, setStatus] = useState(() => {
    const allConfirmed = proposals.every((p) => p.status === "confirmed");
    const allCancelled = proposals.every((p) => p.status === "rejected" || p.status === "cancelled");
    if (allConfirmed) return "confirmed";
    if (allCancelled) return "cancelled";
    return "pending";
  });
  const [loading, setLoading] = useState(false);

  // Sync with parent when proposals are externally cancelled
  useEffect(() => {
    const allCancelled = proposals.every((p) => p.status === "rejected" || p.status === "cancelled");
    if (allCancelled && status === "pending") {
      setStatus("cancelled");
    }
  }, [proposals]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // Only submit proposals that are still pending (skip already confirmed from partial retries)
      const pendingProposals = proposals.filter((p) => p.status === "pending");
      if (pendingProposals.length === 0) {
        setStatus("confirmed");
        setLoading(false);
        return;
      }
      const actions = pendingProposals.map((p) => ({
        proposal_id: p.proposal_id,
        action: "confirm",
      }));

      const res = await postRightworkAPI(API_ops_assistant_bulk_confirm, {
        store_id: homeStore,
        session_uuid: sessionUuid,
        actions: actions,
      });

      if (res.status === 200) {
        const results = res?.data?.redux?.payload?.results || [];
        const succeeded = results.filter((r) => !r.result?.error).map((r) => r.proposal_id);
        const failed = results.filter((r) => r.result?.error);

        if (succeeded.length > 0) {
          onStatusChange?.(succeeded, "confirmed");
        }
        if (failed.length > 0) {
          const failedMsgs = [...new Set(failed.map((r) => r.result?.error).filter(Boolean))];
          if (failedMsgs.length > 0) {
            failedMsgs.forEach((msg) => toast.error(msg));
          } else {
            toast.error(`${failed.length} action${failed.length !== 1 ? "s" : ""} failed.`);
          }
        }
        setStatus(failed.length === 0 ? "confirmed" : succeeded.length > 0 ? "partial" : "pending");
      } else {
        toast.error("Failed to process actions.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process actions.");
    }
    setLoading(false);
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      // Only cancel proposals that are still pending
      const pendingProposals = proposals.filter((p) => p.status === "pending");
      if (pendingProposals.length === 0) {
        setStatus("cancelled");
        setLoading(false);
        return;
      }
      const actions = pendingProposals.map((p) => ({
        proposal_id: p.proposal_id,
        action: "reject",
      }));

      const res = await postRightworkAPI(API_ops_assistant_bulk_confirm, {
        store_id: homeStore,
        session_uuid: sessionUuid,
        actions: actions,
      });

      if (res.status === 200) {
        const results = res?.data?.redux?.payload?.results || [];
        const succeeded = results.filter((r) => !r.result?.error).map((r) => r.proposal_id);
        const failed = results.filter((r) => r.result?.error);

        if (succeeded.length > 0) {
          onStatusChange?.(succeeded, "cancelled");
        }
        if (failed.length > 0) {
          const failedMsgs = [...new Set(failed.map((r) => r.result?.error).filter(Boolean))];
          if (failedMsgs.length > 0) {
            failedMsgs.forEach((msg) => toast.error(msg));
          } else {
            toast.error(`${failed.length} action${failed.length !== 1 ? "s" : ""} could not be cancelled.`);
          }
        }
        setStatus(failed.length === 0 ? "cancelled" : "pending");
      } else {
        toast.error("Failed to cancel actions.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to cancel actions.");
    }
    setLoading(false);
  };

  // Collapsed confirmed state — always green (actions were successfully executed)
  if (status === "confirmed") {
    return (
      <div className="my-2 rounded-lg bg-green-50 border border-green-200 overflow-hidden">
        <div className="px-3 py-2 space-y-1.5">
          {proposals.map((p) => {
            const summary = p.summary || {};
            return (
              <div key={p.proposal_id} className="flex items-start gap-2 text-sm text-green-700">
                <CheckCircleIcon fontSize="small" className="text-green-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="font-medium">{summary.title}</span>
                  {summary.employee_name && (
                    <span className="opacity-80"> — {summary.employee_name}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Collapsed cancelled state
  if (status === "cancelled") {
    return (
      <div className="my-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500">
        <CancelIcon fontSize="small" />
        <span>Actions cancelled</span>
      </div>
    );
  }

  // Pending state — static list with confirm/cancel
  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <HourglassEmptyIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          {proposals.length} Pending Action{proposals.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Items — static, no checkboxes */}
      <div className="px-3 py-2 space-y-2">
        {proposals.map((p) => {
          const summary = p.summary || {};
          const isApproval = summary.action === "APPROVED";

          return (
            <div key={p.proposal_id} className="flex items-start gap-2 text-sm">
              {isApproval ? (
                <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
              ) : (
                <CancelIcon fontSize="small" className="text-red-500 mt-0.5 shrink-0" />
              )}
              <div className="text-gray-700 leading-snug min-w-0">
                <div className="font-medium">{summary.title}</div>
                {summary.employee_name && (
                  <div className="text-gray-500 text-xs">{summary.employee_name}</div>
                )}
                {summary.dates && (
                  <div className="text-gray-500 text-xs">{formatDate(summary.dates)}</div>
                )}
                {summary.date && !summary.dates && (
                  <div className="text-gray-500 text-xs">{formatDate(summary.date)}</div>
                )}
                {summary.type && (
                  <div className="text-gray-500 text-xs">{summary.type}</div>
                )}
                {summary.effective_date && (
                  <div className="text-gray-500 text-xs">Effective {formatDate(summary.effective_date)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={handleCancel}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Confirm
        </button>
      </div>
    </div>
  );
}
